/**
 * Keyword Parsing & Validation Utility
 * Vidyajyoti College of Theology Library System
 *
 * Ensures librarians add concise subject headings and tags rather than
 * pasting full paragraphs or sentences.
 */

export const MAX_KEYWORDS = 10;
export const MAX_KEYWORD_LENGTH = 40;
export const MAX_WORDS_PER_KEYWORD = 5;

/**
 * Parses a comma-separated string or array into sanitized, unique keyword tags.
 * @param {string|string[]} input - Comma-separated string or array of tags
 * @returns {string[]} Sanitized list of unique keyword strings
 */
export function parseKeywords(input) {
  if (!input) return [];
  const rawList = Array.isArray(input) ? input : String(input).split(',');
  const uniqueSet = new Set();

  rawList.forEach((item) => {
    const cleaned = String(item).trim();
    if (cleaned.length > 0) {
      uniqueSet.add(cleaned);
    }
  });

  return Array.from(uniqueSet);
}

/**
 * Validates a list or string of keywords against library cataloguing rules.
 * @param {string|string[]} input - Comma-separated string or array
 * @param {boolean} required - Whether at least 1 keyword is required
 * @returns {{ valid: boolean, error: string | null, tags: string[] }}
 */
export function validateKeywords(input, required = true) {
  const tags = parseKeywords(input);

  if (required && tags.length === 0) {
    return {
      valid: false,
      error: 'Keywords are mandatory. Please enter at least 1 keyword.',
      tags
    };
  }

  if (tags.length > MAX_KEYWORDS) {
    return {
      valid: false,
      error: `Maximum ${MAX_KEYWORDS} keywords allowed. You have entered ${tags.length} keywords.`,
      tags
    };
  }

  for (const tag of tags) {
    // 1. Character length check per individual tag
    if (tag.length > MAX_KEYWORD_LENGTH) {
      const preview = tag.length > 25 ? `${tag.slice(0, 25)}...` : tag;
      return {
        valid: false,
        error: `Keyword "${preview}" is too long (${tag.length} characters). Keywords must be concise terms (max ${MAX_KEYWORD_LENGTH} chars), not sentences or paragraphs.`,
        tags
      };
    }

    // 2. Word count check (prevents full sentence strings without commas)
    const wordCount = tag.split(/\s+/).filter(Boolean).length;
    if (wordCount > MAX_WORDS_PER_KEYWORD) {
      const preview = tag.length > 25 ? `${tag.slice(0, 25)}...` : tag;
      return {
        valid: false,
        error: `Keyword "${preview}" has ${wordCount} words. Keywords must be concise terms (max ${MAX_WORDS_PER_KEYWORD} words). Place paragraphs in Abstract/Summary instead.`,
        tags
      };
    }

    // 3. Sentence punctuation check
    if (tag.endsWith('.') || tag.includes('. ') || tag.includes(';')) {
      const preview = tag.length > 25 ? `${tag.slice(0, 25)}...` : tag;
      return {
        valid: false,
        error: `Keyword "${preview}" contains sentence punctuation. Use the Abstract / Summary field for full sentences.`,
        tags
      };
    }
  }

  return { valid: true, error: null, tags };
}
